import { LogOut, Moon, Sun } from "lucide-react";
import yn from "yn";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import useUser from "~/contexts/user";
import { zendeskUrl } from "~/utils/zendesk";

const { VITE_SUPPORT_EMAIL, VITE_HELP_CENTER_ENABLED } = import.meta.env;

interface ManifoldNextGenAccountMenuProps {
  isDark: boolean;
  onToggleDark: () => void;
  onSwitchToClassic: () => void;
}

export function ManifoldNextGenAccountMenu({
  isDark,
  onToggleDark,
  onSwitchToClassic,
}: ManifoldNextGenAccountMenuProps) {
  const user = useUser();
  const helpCenterEnabled = yn(VITE_HELP_CENTER_ENABLED, { default: true });
  const newTabProps = { target: "_blank", rel: "noopener noreferrer" };
  const initials =
    `${user?.given_name?.[0] ?? ""}${user?.family_name?.[0] ?? ""}` ||
    user?.email?.[0]?.toUpperCase();
  const displayName = user?.name ?? user?.email ?? "";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          aria-label="Account menu"
          className="size-9 rounded-full p-0 text-muted-foreground hover:bg-black/[0.06] hover:text-foreground focus-visible:ring-0"
        >
          <Avatar className="size-7 shrink-0">
            <AvatarImage src={user?.picture} alt={displayName} />
            <AvatarFallback className="text-xs">{initials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="bottom" align="end" className="w-56">
        <DropdownMenuLabel className="flex flex-col gap-0.5">
          <span className="truncate font-medium">{displayName}</span>
          {user?.name && (
            <span className="text-muted-foreground truncate text-xs font-normal">{user.email}</span>
          )}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onToggleDark}>
          {isDark ? <Sun /> : <Moon />}
          {isDark ? "Light mode" : "Dark mode"}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onSwitchToClassic}>Switch to classic look</DropdownMenuItem>
        <DropdownMenuSeparator />
        {helpCenterEnabled && (
          <DropdownMenuItem asChild>
            <a href={zendeskUrl()} {...newTabProps}>
              Help Center
            </a>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem asChild>
          <a
            href={VITE_SUPPORT_EMAIL ? `mailto:${VITE_SUPPORT_EMAIL}` : zendeskUrl("/requests/new")}
            {...(!VITE_SUPPORT_EMAIL ? newTabProps : {})}
          >
            Contact Support
          </a>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <a href="/logout">
            <LogOut />
            Sign Out
          </a>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
